import {
  ActivityFeed,
  ActivityFeedUnion,
  ActivityType,
  AUTOMATION_ACTOR_DISPLAY_NAME,
  AUTOMATION_ACTOR_ID,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  IconNames,
  Shop,
} from "@/types/v2";
import { getCardFieldLabel, getCardSection } from "@/lib/cards";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { format } from "date-fns";
import { useUserQuery } from "@/hooks/queries/useUserQuery";
import { Spinner } from "@/components/Common/Spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/Base/Avatar";
import { getUsersInitials } from "@/lib/users";
import { getIntegratorImage, getShopHeroLogo } from "@/lib/integrators";
import { Icon } from "@/components/Common/Icon";
import { CardLink } from "./CardLink";

export const ActivityFeedItem = ({
  item,
  shop,
  isFirst = false,
  isLast = false,
}: {
  item: ActivityFeed | ActivityFeedUnion;
  shop: Shop;
  isFirst?: boolean;
  isLast?: boolean;
}) => {
  const createdAt = firestoreToDateTime(item.createdAt);
  const { data, isLoading } = useUserQuery(item.modifiedBy);

  const getAvatarImage = () => {
    // Automation entries should always show the Shop Hero logo,
    // not the shop's integrator logo.
    if (item.modifiedBy === AUTOMATION_ACTOR_ID) {
      return getShopHeroLogo();
    }
    return getIntegratorImage(shop.integration ?? "manual");
  };

  // Helper function to render card field with clickable link
  const renderCardFieldWithLink = () => (
    <>
      {getCardFieldLabel(shop, "primaryField")}{" "}
      <CardLink cardId={item.cardId} className="font-bold">
        {getCardSection(item, shop, "primaryField")}
      </CardLink>
    </>
  );

  const renderActivityDescription = () => {
    // Constants for activity actions and descriptions
    const ACTIONS = {
      ADDED: "Added",
      UPDATED: "Updated",
      DELETED: "Deleted",
      STARTED: "Started",
      PAUSED: "Paused",
      RESET: "Reset",
      RESTARTED: "Restarted",
      CHANGED: "Changed",
      REMOVED: "Removed",
    } as const;

    const EXTRA_INFO = {
      NOTE: <>a note</>,
      TIMER: <>timer</>,
      // Distinct from TIMER so a countdown restarted in place ("Reset the
      // running timer") never reads as the timer having been stopped
      // ("Reset timer").
      RUNNING_TIMER: <>the running timer</>,
      CUSTOMER_UPDATE: <>customer update</>,
    } as const;

    // Helper function for standard activity pattern
    const renderStandardActivityDescription = (
      action: string,
      extraInfo?: JSX.Element
    ) => (
      <span>
        {getUserDisplayName()} <span className="font-bold">{action}</span>
        {extraInfo && <> {extraInfo}</>} on {renderCardFieldWithLink()}
      </span>
    );

    const renderNoteActivityDescription = (action: string) => {
      return (
        <span>
          <div>
            {getUserDisplayName()} <span className="font-bold">{action}</span>{" "}
            {EXTRA_INFO.NOTE} on {renderCardFieldWithLink()}:
          </div>
          <div
            className={cn(
              "ml-4 mt-1 flex flex-row items-center gap-1 rounded-md border border-primary/80 bg-primary/20 p-2 text-secondary",
              action === ACTIONS.DELETED && "line-through"
            )}
          >
            {action === ACTIONS.UPDATED && "previousNoteText" in item ? (
              <>
                <span className="line-through">{item.previousNoteText}</span>
                <Icon name={IconNames.rightArrow} size={16} />
              </>
            ) : (
              ""
            )}
            {"noteText" in item ? item.noteText : "Unknown"}
          </div>
        </span>
      );
    };

    // Helper function for customer update activities
    const renderCustomerUpdateDescription = (status: CustomerUpdateStatus) =>
      renderStandardActivityDescription(
        CustomerUpdateStatusLabels[status],
        EXTRA_INFO.CUSTOMER_UPDATE
      );

    // Helper function for tag activities
    const renderTagDescription = (action: string) => {
      const tagInfo = (
        <>
          tag:{" "}
          <span className="font-bold text-blue-600">
            {"tagLabel" in item ? item.tagLabel : "Unknown"}
          </span>
        </>
      );
      return renderStandardActivityDescription(action, tagInfo);
    };

    // Handle legacy format (no activityType field)
    if (!("activityType" in item) || !item.activityType) {
      return renderContainerMovement();
    }

    switch (item.activityType) {
      case ActivityType.CONTAINER_MOVEMENT:
        return renderContainerMovement();
      case ActivityType.NOTE_ADDED:
        return renderNoteActivityDescription(ACTIONS.ADDED);
      case ActivityType.NOTE_UPDATED:
        return renderNoteActivityDescription(ACTIONS.UPDATED);
      case ActivityType.NOTE_DELETED:
        return renderNoteActivityDescription(ACTIONS.DELETED);
      case ActivityType.TIMER_STARTED:
        return renderStandardActivityDescription(
          ACTIONS.STARTED,
          EXTRA_INFO.TIMER
        );
      case ActivityType.TIMER_PAUSED:
        return renderStandardActivityDescription(
          ACTIONS.PAUSED,
          EXTRA_INFO.TIMER
        );
      case ActivityType.TIMER_RESET:
        return renderStandardActivityDescription(
          ACTIONS.RESET,
          EXTRA_INFO.TIMER
        );
      case ActivityType.TIMER_RESTARTED:
        return renderStandardActivityDescription(
          ACTIONS.RESTARTED,
          EXTRA_INFO.TIMER
        );
      case ActivityType.TIMER_RESET_WHILE_RUNNING:
        return renderStandardActivityDescription(
          ACTIONS.RESET,
          EXTRA_INFO.RUNNING_TIMER
        );
      case ActivityType.TIMER_CHANGED:
        return renderStandardActivityDescription(
          ACTIONS.CHANGED,
          EXTRA_INFO.TIMER
        );
      case ActivityType.TAG_ADDED:
        return renderTagDescription(ACTIONS.ADDED);
      case ActivityType.TAG_REMOVED:
        return renderTagDescription(ACTIONS.REMOVED);
      case ActivityType.CUSTOMER_UPDATE_CREATED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.CREATED);
      case ActivityType.CUSTOMER_UPDATE_SCHEDULED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.SCHEDULED);
      case ActivityType.CUSTOMER_UPDATE_COMPLETED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.COMPLETED);
      case ActivityType.CUSTOMER_UPDATE_CANCELLED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.CANCELLED);
      case ActivityType.CUSTOMER_UPDATE_SNOOZED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.SNOOZED);
      case ActivityType.CUSTOMER_UPDATE_SKIPPED:
        return renderCustomerUpdateDescription(CustomerUpdateStatus.SKIPPED);
      default:
        return renderContainerMovement();
    }
  };

  const renderContainerMovement = () => {
    if (!("fromContainerName" in item && "toContainerName" in item)) {
      return <span>performed an action</span>;
    }

    const renderCardInfo = () => <>{renderCardFieldWithLink()} </>;

    const renderUserAttribution = () => (
      <>
        {" by "}
        {getUserDisplayName()}
      </>
    );

    const renderContainerName = (containerName: string) => (
      <span className="font-bold">{containerName}</span>
    );

    const renderMovedBetweenContainers = () => (
      <>
        {renderCardInfo()}
        moved from {renderContainerName(item.fromContainerName!)} to{" "}
        {renderContainerName(item.toContainerName!)}
        {renderUserAttribution()}
      </>
    );

    const renderAddedToBoard = () => (
      <>
        {renderCardInfo()}
        <span className="font-bold">Added</span> to board and{" "}
        {renderContainerName(item.toContainerName!)}
        {renderUserAttribution()}
      </>
    );

    const renderRemovedFromBoard = () => (
      <>
        {renderCardInfo()}
        <span className="font-bold">Removed</span> from board and{" "}
        {renderContainerName(item.fromContainerName!)}
        {renderUserAttribution()}
      </>
    );

    if (item.fromContainerName && item.toContainerName) {
      return renderMovedBetweenContainers();
    }

    if (!item.fromContainerName && item.toContainerName) {
      return renderAddedToBoard();
    }

    if (item.fromContainerName && !item.toContainerName) {
      return renderRemovedFromBoard();
    }

    return <span>performed an action</span>;
  };

  const getUserDisplayName = () => {
    if (isLoading) {
      return <Spinner />;
    }

    // Check automation attribution before modifiedByDisplayName: the backend
    // always populates modifiedByDisplayName with the plain string
    // "Automation" for automation-driven activities, which would otherwise
    // short-circuit this badge below and render unstyled text.
    if (item.modifiedBy === AUTOMATION_ACTOR_ID) {
      return (
        <span className="font-bold text-orange-500">
          {AUTOMATION_ACTOR_DISPLAY_NAME}
        </span>
      );
    }

    // Use modifiedByDisplayName if available (for new activities)
    if ("modifiedByDisplayName" in item && item.modifiedByDisplayName) {
      return item.modifiedByDisplayName;
    }

    // Fall back to user data or system/integration
    if (data) {
      return data.displayName;
    }

    if (item.modifiedBy === "system") {
      return <span className="font-bold text-orange-500">Integration</span>;
    }

    return <span>{item.modifiedBy}</span>;
  };

  return (
    <div className="relative text-secondary">
      <div className="grid grid-cols-5 gap-1 lg:grid-cols-9">
        <div className="relative w-10">
          {/* Connecting line from header dot to avatar */}
          {isFirst && (
            <div className="absolute left-1/2 top-0 h-2 w-0.5 -translate-x-0.5 bg-border"></div>
          )}

          {/* Connecting line between activity items */}
          {!isLast && (
            <div className="absolute bottom-0 left-1/2 h-full w-0.5 -translate-x-0.5 bg-border"></div>
          )}

          {isLast && (
            <div className="absolute left-1/2 top-0 h-1/2 w-0.5 -translate-x-0.5 bg-border"></div>
          )}

          <Avatar className="relative z-10 mt-2 w-10">
            {!isLoading && !data?.displayName && (
              <AvatarImage src={getAvatarImage()} />
            )}
            <AvatarFallback className="bg-secondary text-secondary-foreground">
              {getUsersInitials(data?.displayName ?? shop.integration)}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="col-span-3 flex-1 py-2 text-left text-sm leading-snug lg:col-span-7">
          {renderActivityDescription()}
        </div>
        <div className="py-2 text-right text-xs text-gray-600">
          {createdAt ? format(createdAt, "h:mm b") : ""}
        </div>
      </div>
    </div>
  );
};
